import { Box } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { CreateBreed, EditBreed } from 'features/breed';

import { BreedsFilters, BreedsTable, breedModel } from 'entities/breed';

import { Breed, ESpecie } from 'shared/api';
import { useDebouncedSearch } from 'shared/lib';
import { ConfirmDialog, LoadingProgress } from 'shared/ui';

export const Breeds = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();

  // Получаем параметры из URL или используем значения по умолчанию
  const [filter, setFilter] = useState(searchParams.get('filter') ?? '');
  const [specie, setSpecie] = useState<ESpecie>((searchParams.get('specie') as ESpecie) ?? ESpecie.Cat);
  const [sortBy, setSortBy] = useState(searchParams.get('sortBy') ?? 'name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>(
    (searchParams.get('sortOrder') as 'asc' | 'desc') ?? 'asc',
  );
  const [page, setPage] = useState(parseInt(searchParams.get('page') ?? '1', 10));
  const [rowsPerPage, setRowsPerPage] = useState(parseInt(searchParams.get('limit') ?? '25', 10));

  // Состояния для редактирования и удаления
  const [editBreed, setEditBreed] = useState<Breed | null>(null);
  const [deleteBreed, setDeleteBreed] = useState<Breed | null>(null);
  const [isEditOpen, setIsEditOpen] = useState(false);

  // Мутации
  const { mutate: createBreed, isLoading: isCreating, isSuccess: isCreateSuccess } = breedModel.createBreedAsync();
  const { mutate: updateBreed, isLoading: isUpdating, isSuccess: isUpdateSuccess } = breedModel.updateBreedAsync();
  const { mutate: deleteBreedMutation, isLoading: isDeleting } = breedModel.deleteBreedAsync();

  // Используем debounce для фильтра чтобы не запускать запрос при каждом символе
  const debouncedFilter = useDebouncedSearch(filter, 250);

  // Создаем объект параметров для API с использованием useMemo
  const params = useMemo(
    () => ({
      filter: debouncedFilter || undefined,
      sortBy,
      sortOrder,
      page,
      limit: rowsPerPage,
      specie,
    }),
    [debouncedFilter, sortBy, sortOrder, page, rowsPerPage, specie],
  );

  const { isLoading, isFetching } = breedModel.getBreedsAsync(params)(dispatch);
  const { breedList, pagination } = breedModel.useBreed();

  // Обновляем URL при изменении параметров (используем debouncedFilter для URL)
  useEffect(() => {
    const newParams: Record<string, string> = {
      page: page.toString(),
      limit: rowsPerPage.toString(),
      sortBy,
      sortOrder,
      specie,
    };

    if (debouncedFilter) newParams.filter = debouncedFilter;

    setSearchParams(newParams, { replace: true });
  }, [debouncedFilter, specie, sortBy, sortOrder, page, rowsPerPage, setSearchParams]);

  // Мемоизируем обработчики с помощью useCallback
  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleRowsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleFilterChange = useCallback((value: string) => {
    setFilter(value);
    setPage(1);
  }, []);

  const handleSpecieChange = useCallback((value: ESpecie) => {
    setSpecie(value);
    setPage(1);
  }, []);

  const handleSortChange = useCallback(
    (columnId: string) => {
      if (sortBy === columnId) {
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
      } else {
        setSortBy(columnId);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sortBy, sortOrder],
  );

  // Обработчики для CRUD операций
  const handleCreateBreed = useCallback(
    (data: Partial<Breed>) => {
      createBreed(data as Omit<Breed, '_id'>);
    },
    [createBreed],
  );

  const handleEditBreed = useCallback((breed: Breed) => {
    setEditBreed(breed);
    setIsEditOpen(true);
  }, []);

  const handleUpdateBreed = useCallback(
    (data: Partial<Breed>) => {
      if (editBreed) {
        const updatedData: Breed = { ...editBreed, ...data };
        updateBreed({ id: editBreed._id, data: updatedData });
      }
    },
    [editBreed, updateBreed],
  );

  const handleDeleteBreed = useCallback((breed: Breed) => {
    setDeleteBreed(breed);
  }, []);

  const handleConfirmDelete = useCallback(() => {
    if (deleteBreed) {
      deleteBreedMutation(deleteBreed._id);
      setDeleteBreed(null);
    }
  }, [deleteBreed, deleteBreedMutation]);

  const handleCancelDelete = useCallback(() => {
    setDeleteBreed(null);
  }, []);

  const handleCloseEdit = useCallback(() => {
    setIsEditOpen(false);
    setEditBreed(null);
  }, []);

  // Показываем LoadingProgress только при первой загрузке (когда еще нет данных)
  if (isLoading && !breedList.length) return <LoadingProgress />;

  return (
    <Box display="flex" flexDirection="column">
      <BreedsFilters
        filter={filter}
        specie={specie}
        totalCount={pagination?.total ?? 0}
        onFilterChange={handleFilterChange}
        onSpecieChange={handleSpecieChange}
        addButton={<CreateBreed onSubmit={handleCreateBreed} isSuccess={isCreateSuccess} isLoading={isCreating} />}
      />

      <BreedsTable
        breeds={breedList || []}
        pagination={pagination}
        page={page}
        rowsPerPage={rowsPerPage}
        sortBy={sortBy}
        sortOrder={sortOrder}
        isLoading={isFetching}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSortChange={handleSortChange}
        onEdit={handleEditBreed}
        onDelete={handleDeleteBreed}
      />

      {/* Edit Dialog */}
      <EditBreed
        breed={editBreed}
        isOpen={isEditOpen}
        onClose={handleCloseEdit}
        onSubmit={handleUpdateBreed}
        isSuccess={isUpdateSuccess}
        isLoading={isUpdating}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={!!deleteBreed}
        title="Delete Breed"
        message={`Are you sure you want to delete the breed "${
          deleteBreed?.name ?? ''
        }"? This action cannot be undone.`}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        isLoading={isDeleting}
      />
    </Box>
  );
};
